import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../../services/contexts/AuthContext';
import { 
  Home,
  Users, 
  Activity, 
  Settings, 
  BarChart3, 
  Wrench,
  Heart,
  Zap,
  Monitor,
  Camera,
  HardDrive,
  Wifi,
  Clock,
  CheckCircle,
  AlertTriangle,
  TrendingUp,
  Shield,
  Database,
  Play,
  Pause,
  Stop,
  Upload,
  Download,
  RefreshCw,
  Power,
  Thermometer,
  Gauge,
  Battery,
  Signal
} from 'lucide-react';

const TenantTechnicianSidebar = ({ activeView, setActiveView }) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const menuItems = [
    {
      id: 'dashboard',
      label: 'Dashboard',
      icon: Home,
      color: 'text-brand-blue',
      path: '/tenant-technician/dashboard'
    },
    {
      id: 'onboarding',
      label: 'Patient Onboarding',
      icon: Users,
      color: 'text-brand-cyan',
      path: '/tenant-technician/onboarding'
    },
    {
      id: 'recordings',
      label: 'Recording Management',
      icon: Activity,
      color: 'text-va-primary-600',
      path: '/tenant-technician/recordings'
    },
    {
      id: 'equipment',
      label: 'Equipment Status',
      icon: Settings,
      color: 'text-va-health-600',
      path: '/tenant-technician/equipment'
    },
    {
      id: 'analytics',
      label: 'Analytics',
      icon: BarChart3,
      color: 'text-va-accent-600',
      path: '/tenant-technician/analytics'
    }
  ];

  const handleNavigation = (item) => {
    setActiveView(item.id);
    navigate(item.path);
  };

  const getCurrentPath = () => {
    return location.pathname.split('/').pop();
  };

  return (
    <div className="w-64 bg-white border-r border-va-neutral-200 flex flex-col h-full">
      {/* Header */}
      <div className="p-6 border-b border-va-neutral-200">
        <div className="flex items-center space-x-3">
          <div className="w-10 h-10 bg-gradient-to-br from-brand-blue to-brand-darkCyan rounded-xl flex items-center justify-center">
            <Wrench className="h-6 w-6 text-white" />
          </div>
          <div>
            <h1 className="text-xl font-bold text-va-neutral-900">VitalAudio</h1>
            <p className="text-sm text-va-neutral-600">Technical Operations</p>
          </div>
        </div>
      </div>

      {/* User Profile */}
      <div className="p-4">
        <div className="bg-va-primary-50 rounded-xl p-4 border border-va-primary-200">
          <div className="flex items-center space-x-3">
            <div className="w-12 h-12 bg-gradient-to-br from-brand-cyan to-va-health-600 rounded-xl flex items-center justify-center">
              <span className="text-white font-bold text-lg">
                {user?.name?.split(' ').map(n => n[0]).join('') || 'T'}
              </span>
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-va-neutral-900 truncate">
                {user?.name || 'Technician'}
              </p>
              <p className="text-xs text-va-neutral-500 truncate">Technical Specialist</p>
            </div>
          </div>
        </div>
      </div>

      {/* Navigation */}
      <nav className="flex-1 px-4 space-y-1">
        <div className="mb-4">
          <p className="text-xs font-semibold text-va-neutral-400 uppercase tracking-wide mb-3">
            OPERATIONS
          </p>
          {menuItems.map((item) => {
            const Icon = item.icon;
            const isActive = getCurrentPath() === item.id || (getCurrentPath() === '' && item.id === 'dashboard');
            
            return (
              <button
                key={item.id}
                onClick={() => handleNavigation(item)}
                className={`w-full flex items-center space-x-3 px-4 py-3 rounded-xl text-left transition-all duration-200 ${
                  isActive 
                    ? 'bg-brand-blue-50 text-brand-blue-700 border border-brand-blue-200 shadow-sm' 
                    : 'text-va-neutral-700 hover:text-va-neutral-900 hover:bg-va-neutral-50'
                }`}
              >
                <Icon className={`h-5 w-5 ${isActive ? 'text-brand-blue' : item.color} transition-colors`} />
                <span className="font-medium">{item.label}</span>
                {isActive && (
                  <div className="ml-auto w-2 h-2 bg-brand-blue rounded-full"></div>
                )}
              </button>
            );
          })}
        </div>

        {/* Equipment Status */}
        <div className="mb-4">
          <h3 className="text-sm font-semibold text-va-neutral-900">Equipment Status</h3>
          <div className="mt-3 space-y-2">
            <div className="flex items-center space-x-2 p-2 bg-va-health-50 rounded-lg">
              <div className="w-2 h-2 bg-va-health-600 rounded-full"></div>
              <span className="text-xs text-va-neutral-700">Audio Capture</span>
            </div>
            <div className="flex items-center space-x-2 p-2 bg-va-health-50 rounded-lg">
              <div className="w-2 h-2 bg-va-health-600 rounded-full"></div>
              <span className="text-xs text-va-neutral-700">Video Capture</span>
            </div>
            <div className="flex items-center space-x-2 p-2 bg-va-health-50 rounded-lg">
              <div className="w-2 h-2 bg-va-health-600 rounded-full"></div>
              <span className="text-xs text-va-neutral-700">Processing Unit</span>
            </div>
          </div>
        </div>

        {/* Collection Stats */}
        <div className="mb-4">
          <h3 className="text-sm font-semibold text-va-neutral-900">Collection Stats</h3>
          <div className="mt-3 space-y-2">
            <div className="flex justify-between items-center p-2 bg-va-primary-50 rounded-lg">
              <span className="text-xs text-va-neutral-700">Today's Recordings</span>
              <span className="text-xs font-medium text-va-neutral-900">12</span>
            </div>
            <div className="flex justify-between items-center p-2 bg-va-primary-50 rounded-lg">
              <span className="text-xs text-va-neutral-700">Success Rate</span>
              <span className="text-xs font-medium text-va-health-600">94%</span>
            </div>
            <div className="flex justify-between items-center p-2 bg-va-primary-50 rounded-lg">
              <span className="text-xs text-va-neutral-700">Avg Duration</span>
              <span className="text-xs font-medium text-va-neutral-900">2.3m</span>
            </div>
          </div>
        </div>

        {/* Quick Actions */}
        <div className="mb-4">
          <h3 className="text-sm font-semibold text-va-neutral-900">Quick Actions</h3>
          <div className="mt-3 space-y-2">
            <button className="w-full text-left p-2 bg-va-accent-50 rounded-lg hover:bg-va-accent-100 transition-colors">
              <div className="flex items-center space-x-2">
                <Upload className="h-4 w-4 text-va-accent-600" />
                <span className="text-xs text-va-neutral-700">Send Links</span>
              </div>
            </button>
            <button className="w-full text-left p-2 bg-va-health-50 rounded-lg hover:bg-va-health-100 transition-colors">
              <div className="flex items-center space-x-2">
                <RefreshCw className="h-4 w-4 text-va-health-600" />
                <span className="text-xs text-va-neutral-700">System Check</span>
              </div>
            </button>
            <button className="w-full text-left p-2 bg-va-warning-50 rounded-lg hover:bg-va-warning-100 transition-colors">
              <div className="flex items-center space-x-2">
                <Settings className="h-4 w-4 text-va-warning-600" />
                <span className="text-xs text-va-neutral-700">Calibrate</span>
              </div>
            </button>
          </div>
        </div>
      </nav>

      {/* Footer */}
      <div className="p-4 border-t border-va-neutral-200">
        <div className="text-center">
          <p className="text-xs text-va-neutral-500">Technical Operations</p>
          <p className="text-xs text-va-neutral-400 mt-1">VitalAudio v1.0</p>
        </div>
      </div>
    </div>
  );
};

export default TenantTechnicianSidebar;